import {
  InfiniteData,
  QueryClient,
  UseInfiniteQueryResult,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

export type ApiFnReturnType<FnType extends (...args: any) => Promise<any>> = Awaited<
  ReturnType<FnType>
>;

export type MutationConfig<MutationFnType extends (...args: any) => Promise<any>> =
  UseMutationOptions<ApiFnReturnType<MutationFnType>, Error, Parameters<MutationFnType>[0]>;

export type QueryConfig<FnType extends (...args: any) => Promise<any>> = {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  onSuccess?: (data: ApiFnReturnType<FnType>) => void;
  onError?: (error: Error) => void;
};

export function useListData<T>(
  query: UseInfiniteQueryResult<InfiniteData<T[], unknown>, unknown>,
  array: any[]
) {
  const queryClient = useQueryClient();
  const onRefresh = useCallback(() => {
    queryClient.setQueryData<InfiniteData<any>>(array, oldData => {
      if (!oldData) {
        return;
      }
      return {
        pages: oldData.pages.slice(0, 1),
        pageParams: oldData.pageParams.slice(0, 1),
      };
    });
    query.refetch();
  }, [array, query, queryClient]);

  const items = useFlatten(query.data);
  return {
    items,
    onRefresh,
  };
}

export function useFlatten<T>(data: InfiniteData<T[]> | undefined): T[] {
  if (!data) {
    return [];
  }
  const items = data.pages.reduce<T[]>((acc, val) => {
    acc.push(...val);
    return acc;
  }, []);
  return items;
}

export function deleteListItem<T>(
  queryClient: QueryClient,
  key: string[],
  filterFn: (item: T) => boolean
) {
  queryClient.setQueriesData<InfiniteData<T[]>>({ queryKey: key }, oldData => {
    if (!oldData) {
      return oldData;
    }
    const newPages = oldData.pages.map(page => {
      const newData = page.filter(filterFn);
      return newData;
    });
    return {
      pageParams: oldData.pageParams,
      pages: newPages,
    };
  });
}

export const queryKeys = {
  pickem: {
    all: ['pickem'] as const,
    lists: () => [...queryKeys.pickem.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.pickem.lists(), filters] as const,
    details: () => [...queryKeys.pickem.all, 'detail'] as const,
    detail: (id: string, location?: string) =>
      [...queryKeys.pickem.details(), id, location] as const,
    upcoming: (location?: string) => [...queryKeys.pickem.all, 'upcoming', location] as const,
    recent: (location?: string) => [...queryKeys.pickem.all, 'recent', location] as const,
    today: (location?: string) => [...queryKeys.pickem.all, 'today', location] as const,
    play: (location?: string) => [...queryKeys.pickem.all, 'play', location] as const,
    rules: (id: string) => [...queryKeys.pickem.all, 'rules', id] as const,
  },
  tenant: {
    all: ['tenant'] as const,
    lists: () => [...queryKeys.tenant.all, 'list'] as const,
    list: (filters: any) => [...queryKeys.tenant.lists(), filters] as const,
    details: () => [...queryKeys.tenant.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tenant.details(), id] as const,
  },
  leaderboard: {
    all: ['leaderboard'] as const,
    pickem: (id: string) => [...queryKeys.leaderboard.all, 'pickem', id] as const,
    global: (period: string) => [...queryKeys.leaderboard.all, 'global', period] as const,
  },
  user: {
    all: ['user'] as const,
    details: () => [...queryKeys.user.all, 'details'] as const,
    raffles: () => [...queryKeys.user.all, 'raffles'] as const,
    streak: () => [...queryKeys.user.all, 'streak'] as const,
  },
};
